import { eTlfItemType } from "functions/api/enums";
import { ITocSection } from "functions/api/hierarchy/ITocSection";
import { MosaicTocBaseClass } from "./MosaicTocItemClass";
import { MosaicTocBaseDto, MosaicTocSectionDto } from "./interface";

export interface ITocSectionExpanded extends ITocSection {
  primaryHierarchyId: string;
  changed?: boolean;
}

/**
 * Type guard to assert an unknown value is an expanded TOC section.
 *
 * @param obj - Value to test for the expanded TOC section shape.
 * @returns `true` when `obj` implements `ITocSectionExpanded`, otherwise `false`.
 */
export const isITocSectionExpanded = (obj: unknown): obj is ITocSectionExpanded => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "id" in obj &&
    "sectionNumber" in obj &&
    "sectionLabel" in obj &&
    "order" in obj &&
    "primaryHierarchyId" in obj
  );
};

/**
 * Represents a TOC section within the MOSAIC editor, extending base TOC properties.
 */
export class MosaicTocSectionClass extends MosaicTocBaseClass {
  public readonly type: eTlfItemType.section = eTlfItemType.section;

  /**
   * Construct a `MosaicTocSectionClass` from either an expanded section or DTO.
   *
   * @param input - Source object either expanded (`ITocSectionExpanded`) or DTO form.
   */
  public constructor(input: ITocSectionExpanded | MosaicTocSectionDto) {
    const feedSuper: MosaicTocBaseDto = isITocSectionExpanded(input)
      ? {
          id: input.id,
          primaryHierarchyId: input.primaryHierarchyId,
          tocLabel: input.sectionLabel,
          tocNumber: input.sectionNumber,
          tocOrder: input.order,
          changed: input.changed ?? false,
          inError: false,
        }
      : input;
    super(feedSuper);
  }

  /**
   * Return the data transfer object representation of this section.
   *
   * @returns A `MosaicTocSectionDto` representing this section's persisted shape.
   */
  get data(): MosaicTocSectionDto {
    return {
      id: this.id,
      type: this.type,
      primaryHierarchyId: this.primaryHierarchyId,
      tocLabel: this.tocLabel,
      tocNumber: this.tocNumber,
      tocOrder: this.tocOrder,
      changed: this.changed,
      inError: this.inError,
    };
  }
}
